import { createElement } from "react";

import type { IShoppingCartAddResponse } from "@voith-webshop/checkout-interfaces";
import type { IClientShopFlags } from "@voith-webshop/core-interfaces";
import type { SagaIterator } from "redux-saga";
import { call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

import * as cartApi from "@/shop/api/shopping/cart";
import AddFailNotice from "@/shop/components/Cart/Messages/AddFailNotice";
import AddQuickBuyMaterialWarning from "@/shop/components/Cart/Messages/AddQuickBuyMaterialWarning";
import { AddSuccessNotice } from "@/shop/components/Cart/Messages/AddSuccessNotice/AddSuccessNotice";
import { displayErrorMessage, displayInfoMessage, displayWarningMessage } from "@/shop/components/common/Messages";
import { fetchFavListsStart } from "@/shop/components/FavList/reducer/favListReducer/actions";
import { getMostRecentFavListUpdate } from "@/shop/components/FavList/reducer/favListReducer/selectors";
import { getIndustryFeatures, getShowFavList } from "@/shop/reducers/session";
import type { IShoppingCartData, IShoppingCartPosition, IShoppingCartSyncState } from "@/shop/types/shopping/cart";
import { rememberRaisedPositions } from "@/shop/utils/minOrderQty";
import updatePositionsSequence from "@/shop/utils/update-positions-sequence";

import * as CartActions from "./actions";
import { mergeListItem } from "./mergeListItems";
import { getCartData, getSyncingCartData } from "./selectors";

import type { CartPositionCollection } from ".";

/**
 * Fetch the cart
 * @param _
 */
function* fetchCart(_: ActionType<typeof CartActions.fetch>): SagaIterator {
  try {
    // flag the tree as fetching
    yield put(CartActions.fetchStart());

    // call the api to retrieve the session
    const cartData = (yield call(cartApi.fetchCart)) as IShoppingCartData;

    const positionsMovedToOrderable = cartData.positions.filter((pos) => pos.movedToOrderable);
    if (positionsMovedToOrderable.length > 0) {
      displayInfoMessage(createElement(AddSuccessNotice, { positions: positionsMovedToOrderable, message: "movedToOrderable" }), 7_500);
    }

    // put action to save data in state
    yield put(CartActions.fetchSuccess(cartData));
  } catch (err) {
    // fetch failed, handle the error
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.fetchFail(err));
  }
}

/**
 * Check cart sync with server
 * @param _
 */
function* checkSync(_: ActionType<typeof CartActions.checkSync>): SagaIterator {
  try {
    yield put(CartActions.checkSyncStart());
    const cartData = (yield select(getCartData)) as IShoppingCartData;
    const mostRecentFavListUpdate = (yield select(getMostRecentFavListUpdate)) as string;
    const hasFavList = (yield select(getShowFavList)) as boolean;

    if (cartApi.isDummyCart(cartData)) {
      return (yield put(CartActions.checkSyncSkip())) as void;
    }

    const syncState = (yield call(cartApi.checkCartSync, cartData.id, cartData.date, hasFavList, mostRecentFavListUpdate)) as IShoppingCartSyncState;

    if (syncState.favListsAreUpToDate === false) {
      yield put(fetchFavListsStart());
    }

    if (syncState.isSync) {
      yield put(CartActions.checkSyncSuccess(syncState));
    } else {
      yield put(CartActions.fetch());
    }
  } catch {
    yield put(CartActions.checkSyncFail());
  }
}

/**
 * Add items to the shopping cart
 * @param action
 */
function* addItems(action: ActionType<typeof CartActions.addItems>): SagaIterator {
  const { thunk } = action.meta;
  try {
    // flag adding as started
    yield put(CartActions.addItemsStart());
    const { showWarningAddingQuickBuyMaterial } = (yield select(getIndustryFeatures)) as IClientShopFlags;

    const items = action.payload;

    const { added, ...cartData } = (yield call(cartApi.addToCart, items)) as IShoppingCartAddResponse;
    yield put(CartActions.addItemsSuccess(cartData as IShoppingCartAddResponse, { thunk }));

    rememberRaisedPositions(added);
    displayInfoMessage(createElement(AddSuccessNotice, { positions: added }));

    // This page shouldn't show quick buy material warnings
    const isOrderHistoryQuoteDetailsPage = window.location.href.includes("order-history/quotes/details");

    if (showWarningAddingQuickBuyMaterial && cartData?.quickBuyMaterialWarnings?.length && !isOrderHistoryQuoteDetailsPage) {
      displayWarningMessage(createElement(AddQuickBuyMaterialWarning, { warnings: cartData?.quickBuyMaterialWarnings }), 8000);
    }
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.addItemsFail(err, { thunk }));
    displayErrorMessage(createElement(AddFailNotice));
  }
}

/**
 * Add items to the shopping cart
 * removes all items from the cart and then adds the new items
 * the callback can be used to redirect to the cart page
 * if the redirect is done immediately the cart page will load the cart
 * and overwrite the removeAllPositions action
 * @param action
 */
function* removeAllItemsFromCartThenAddItems({ payload }: ActionType<typeof CartActions.removeAllItemsFromCartThenAddItems>): SagaIterator {
  try {
    const { items, collections, callback } = payload;
    yield call(removeAllPositions, CartActions.removeAllPositions({ collection: collections, awaitable: true }));
    yield call(addItems, CartActions.addItems(items));
    if (callback != null) {
      callback();
    }
  } catch (err) {
    console.error(err);
  }
}

/**
 * Moves positions in the cart to onRequestPositions
 * TODO: this should be moved to the server at some point.
 */
function* movePositionsToRequestOnly(action: ActionType<typeof CartActions.moveToOnRequestPositions>): SagaIterator {
  const missingIds = action.payload;
  try {
    if (missingIds.length > 0) {
      const cartData = (yield select(getCartData)) as IShoppingCartData;
      const nextPositions = cartData.positions.filter((pos) => missingIds.indexOf(pos.id) < 0);

      const additionalRequestPositions = cartData.positions.filter((pos) => missingIds.indexOf(pos.id) >= 0);

      if (additionalRequestPositions.length > 0) {
        const requestItems = cartData.onRequestPositions;

        // clone the positions since we need to modify them
        const nextRequestItems = JSON.parse(JSON.stringify(requestItems)) as IShoppingCartPosition[];

        // merge items
        additionalRequestPositions.forEach((item) => {
          mergeListItem(nextRequestItems, item);
        });

        const nextCart: IShoppingCartData = { ...cartData, positions: nextPositions, onRequestPositions: nextRequestItems };
        yield put(CartActions.update(nextCart));
      }
    }
  } catch (err) {
    console.log(err);
  }
}

/**
 * Update the shopping cart
 * @param action
 */
function* updateCart(action: ActionType<typeof CartActions.update>): SagaIterator<void> {
  try {
    yield put(CartActions.updateStart(action.payload));
    const cartData = (yield call(cartApi.updateCart, action.payload)) as IShoppingCartData;
    yield put(CartActions.updateSuccess(cartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

function* patchCartPosition(collection: CartPositionCollection, positionId: string, patchData: Partial<Omit<IShoppingCartPosition, "id">>): SagaIterator<void> {
  try {
    const syncingCartData = (yield select(getSyncingCartData)) as IShoppingCartData;
    const cartData: IShoppingCartData = syncingCartData || (yield select(getCartData));

    const collectionPositions = cartData[collection].map((otherPosition) =>
      otherPosition.id === positionId
        ? {
            ...otherPosition,
            ...patchData
          }
        : otherPosition
    );

    const nextCartData = {
      ...cartData,
      [collection]: collectionPositions
    };

    yield put(CartActions.update(nextCartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

/**
 * Change the quantity of a shopping cart position
 * @param action
 */
function* changeQuantity(action: ActionType<typeof CartActions.changeQuantity>): SagaIterator<void> {
  const { collection, id, qty } = action.payload;
  yield call(patchCartPosition, collection, id, { qty });
}

function* updateCustomerMaterialNumber(action: ActionType<typeof CartActions.updateCustomerMaterialNumber>): SagaIterator<void> {
  const { collection, id, newCustomerMaterialNumber } = action.payload;
  yield call(patchCartPosition, collection, id, { newCustomerMaterialNumber });
}

function* updateSequenceNumber(action: ActionType<typeof CartActions.updateSequenceNumber>): SagaIterator<void> {
  const { collection, id, sequenceNumber } = action.payload;
  yield call(patchCartPosition, collection, id, { sequenceNumber });
}

/**
 * Update all sequence numbers in the cart
 * @param action
 */
function* updateAllSequenceNumbers(action: ActionType<typeof CartActions.updateAllSequenceNumbers>): SagaIterator {
  try {
    const { collection, updatedIdsSequence } = action.payload;
    const cartData = (yield select(getCartData)) as IShoppingCartData;

    const collectionPositions = updatePositionsSequence<IShoppingCartPosition>(cartData[collection], updatedIdsSequence);

    const nextCartData = {
      ...cartData,
      [collection]: collectionPositions
    };
    yield put(CartActions.update(nextCartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

function* updatePositionComment(action: ActionType<typeof CartActions.updatePositionComment>): SagaIterator<void> {
  const { collection, id, comment } = action.payload;
  yield call(patchCartPosition, collection, id, { comment });
}

function* updatePositionEquipmentNumber(action: ActionType<typeof CartActions.updatePositionEquipmentNumber>): SagaIterator<void> {
  const { collection, id, equipmentNumber } = action.payload;
  yield call(patchCartPosition, collection, id, { equipmentNumber });
}

function* updatePositionRollNumber(action: ActionType<typeof CartActions.updatePositionRollNumber>): SagaIterator<void> {
  const { collection, id, rollNumber } = action.payload;
  yield call(patchCartPosition, collection, id, { rollNumber });
}

/**
 * Removes a position form the shopping cart.
 * @param action
 */
function* removeCartPosition(action: ActionType<typeof CartActions.removePosition>): SagaIterator {
  try {
    const { collection, id } = action.payload;
    const cartData = (yield select(getCartData)) as IShoppingCartData;

    const collectionPositions = cartData[collection].filter((pos) => pos.id !== id);

    const nextCartData = {
      ...cartData,
      [collection]: collectionPositions
    };
    yield put(CartActions.update(nextCartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

/**
 * Toggles cart position enabled status
 * @param action
 */
function* toggleCartPosition(action: ActionType<typeof CartActions.togglePosition>): SagaIterator {
  try {
    const { collection, id, enabled } = action.payload;
    const cartData = (yield select(getCartData)) as IShoppingCartData;

    const collectionPositions = cartData[collection].map((pos) =>
      pos.id === id
        ? {
            ...pos,
            enabled: enabled == null ? !pos.enabled : enabled
          }
        : pos
    );

    const nextCartData = {
      ...cartData,
      [collection]: collectionPositions
    };
    yield put(CartActions.update(nextCartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

/**
 * Toggle all cart positions enabled status
 * @param action
 */
function* toggleAllCartPositions(action: ActionType<typeof CartActions.toggleAllPositions>): SagaIterator {
  try {
    const { collection, enabled } = action.payload;
    const cartData = (yield select(getCartData)) as IShoppingCartData;

    const allEnabled = enabled != null ? enabled : !cartData[collection].some((p) => p.enabled);

    const collectionPositions = cartData[collection].map((pos) => ({
      ...pos,
      enabled: enabled == null ? allEnabled : enabled
    }));

    const nextCartData = {
      ...cartData,
      [collection]: collectionPositions
    };
    yield put(CartActions.update(nextCartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

function* removeAllPositions({ payload }: ActionType<typeof CartActions.removeAllPositions>): SagaIterator {
  try {
    const { collection, awaitable } = payload;
    const cartData = (yield select(getCartData)) as IShoppingCartData;

    const nextCartData = Array.isArray(collection) ? collection.reduce(replaceCollection, cartData) : replaceCollection(cartData, collection);

    // In certain cases we need to wait for the update to finish e.g. to remove all positions and add new ones
    yield awaitable ? call(updateCart, CartActions.update(nextCartData)) : put(CartActions.update(nextCartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

function replaceCollection(cartData: IShoppingCartData, collection: CartPositionCollection): IShoppingCartData {
  return { ...cartData, [collection]: [] };
}

function* splitCartPosition(action: ActionType<typeof CartActions.splitCartPosition>): SagaIterator<void> {
  const { collection, targetPosition } = action.payload;
  try {
    const cartData = (yield select(getCartData)) as IShoppingCartData;

    const collectionPositions = cartData[collection];
    collectionPositions.push({
      ...targetPosition,
      id: self.crypto.randomUUID(),
      qty: targetPosition.minOrderQty != null ? targetPosition.minOrderQty : 1
    });

    const nextCartData = {
      ...cartData,
      [collection]: collectionPositions
    };

    yield put(CartActions.update(nextCartData));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(CartActions.updateFail(err));
  }
}

// TODO: consider use of takeLeading vs takeLatest
export default function* treeSaga(): SagaIterator {
  // cart operations
  yield takeLatest(getType(CartActions.fetch), fetchCart);
  yield takeLatest(getType(CartActions.update), updateCart);
  yield takeLatest(getType(CartActions.checkSync), checkSync);

  // position operations
  yield takeEvery(getType(CartActions.addItems), addItems);
  yield takeLatest(getType(CartActions.removeAllItemsFromCartThenAddItems), removeAllItemsFromCartThenAddItems);
  yield takeLatest(getType(CartActions.changeQuantity), changeQuantity);
  yield takeLatest(getType(CartActions.removePosition), removeCartPosition);
  yield takeLatest(getType(CartActions.togglePosition), toggleCartPosition);
  yield takeLatest(getType(CartActions.toggleAllPositions), toggleAllCartPositions);
  yield takeLatest(getType(CartActions.updateCustomerMaterialNumber), updateCustomerMaterialNumber);
  yield takeLatest(getType(CartActions.updateSequenceNumber), updateSequenceNumber);
  yield takeLatest(getType(CartActions.updateAllSequenceNumbers), updateAllSequenceNumbers);
  yield takeLatest(getType(CartActions.updatePositionComment), updatePositionComment);
  yield takeLatest(getType(CartActions.updatePositionEquipmentNumber), updatePositionEquipmentNumber);
  yield takeLatest(getType(CartActions.updatePositionRollNumber), updatePositionRollNumber);
  yield takeLatest(getType(CartActions.splitCartPosition), splitCartPosition);
  yield takeLeading(getType(CartActions.moveToOnRequestPositions), movePositionsToRequestOnly);

  yield takeLeading(getType(CartActions.removeAllPositions), removeAllPositions);
}
